import { useRef } from 'react'
import { Plus, Type } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import type { EditSpecFormatDict } from '../../../../generated/nodeDataTypes'
import { createNadDebug } from '../../../../utils/debugLog'
import { TextInspector } from './TextInspector'
import { TextTrackLanes } from './TextTrackLanes'
import { addText, deleteText, duplicateText, textInspectorDefaults, textTrackItems, textTrackLanes, updateText, TEXT_LANE_HEIGHT, type TextDocument, type TextPatch } from './textTrack'
import type { TimelineState } from './types'
import { LEGACY_TIMELINE_FPS } from './timelineNavigation'

const log = createNadDebug('dw:editor-manual-text-controls-media')
export const DEFAULT_TEXT_FORMAT: EditSpecFormatDict = { aspect: '16:9', width: 1920, height: 1080, fps: LEGACY_TIMELINE_FPS }

export function useLegacyTextTracks({ state, selectedKey, onSelect, onCommit, disabled, nodeId, onSeek, sourceFormat }: {
  sourceFormat?: EditSpecFormatDict
  state: TimelineState; selectedKey: string | null; onSelect: (key: string | null) => void
  onCommit: (overlays: NonNullable<TimelineState['textOverlays']>, format: EditSpecFormatDict) => void
  disabled: boolean; nodeId: string; onSeek: (time: number) => void
}) {
  const { t } = useTranslation('canvas')
  const document = { overlays: state.textOverlays ?? [], format: state.textFormat ?? sourceFormat ?? DEFAULT_TEXT_FORMAT }
  const latest = useRef(document)
  latest.current = document
  const gesture = useRef<Array<{ key: string; patch: TextPatch }> | null>(null)
  const items = textTrackItems(document)
  const lanes = textTrackLanes(items)
  const selected = items.find((item) => item.key === selectedKey)
  function commit(next: TextDocument, event = 'text-change') {
    if (disabled) return
    latest.current = { ...latest.current, overlays: next.overlays ?? [] }
    onCommit(latest.current.overlays, latest.current.format)
    log({ event, node_id: nodeId, text_count: latest.current.overlays.length })
  }
  function change(key: string, patch: TextPatch) {
    if (disabled) return
    if (gesture.current) { gesture.current.push({ key, patch }); return }
    try { commit(updateText(latest.current, key, patch)) }
    catch (cause) { reportInvalidEdit(cause instanceof Error ? cause.message : String(cause)) }
  }
  function reportInvalidEdit(message: string) {
    log({ event: 'text-change-rejected', node_id: nodeId, error: message })
    toast.error(message)
  }
  function begin() { gesture.current = [] }
  function finish(key: string, patch: TextPatch | null) {
    const drafts = gesture.current ?? []
    gesture.current = null
    if (disabled || (!patch && !drafts.length)) return
    try {
      const next = drafts.reduce((doc, draft) => updateText(doc, draft.key, draft.patch), latest.current)
      commit(patch ? updateText(next, key, patch) : next, 'text-gesture-commit')
    } catch (cause) { reportInvalidEdit(cause instanceof Error ? cause.message : String(cause)) }
  }
  function select(key: string) {
    onSelect(key)
    const item = items.find((item) => item.key === key)
    if (item && (state.playheadPosition < item.start_seconds || state.playheadPosition >= item.start_seconds + item.duration_seconds)) onSeek(item.start_seconds)
  }
  function add(cta = false) {
    if (disabled) return
    const id = crypto.randomUUID()
    commit(addText(latest.current, id, state.playheadPosition, t(cta ? 'textTrack.newCta' : 'textTrack.newText'), cta), 'text-add')
    onSelect(`overlay:${id}`)
  }
  const button = 'flex h-7 items-center gap-1 rounded-lg border border-input-border bg-surface-1 px-2 text-sm text-foreground hover:bg-surface-3 hover:border-input-border-hover focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40'
  return {
    document, add, begin, finish,
    remove: () => { if (selected) { commit(deleteText(latest.current, selected.key), 'text-delete'); onSelect(null) } },
    toolbar: <div className="flex h-9 shrink-0 items-center gap-2 border-b border-border bg-surface-0 px-3" data-testid="text-track-toolbar">
      <Type className="h-4 w-4 text-muted-foreground" /><span className="mr-auto text-sm font-medium">{t('textTrack.track')}</span>
      <button type="button" disabled={disabled} onClick={() => add()} className={button}><Plus className="h-3 w-3" />{t('textTrack.add')}</button>
      <button type="button" disabled={disabled} onClick={() => add(true)} className={button}>{t('textTrack.addCta')}</button>
    </div>,
    inspector: selected ? <TextInspector item={selected} defaults={textInspectorDefaults(document, selected)} disabled={disabled}
      onChange={(patch) => change(selected.key, patch)}
      onDelete={() => { commit(deleteText(latest.current, selected.key), 'text-delete'); onSelect(null) }}
      onDuplicate={() => { const result = duplicateText(latest.current, selected.key, crypto.randomUUID()); commit(result.spec, 'text-duplicate'); onSelect(result.key) }} /> : null,
    tracks: { height: items.length ? lanes.length * TEXT_LANE_HEIGHT : 0,
      header: items.length ? lanes.map((_lane, index) => <div key={index} className="flex shrink-0 items-center gap-2 border-b border-border-subtle px-2 text-xs" style={{ height: TEXT_LANE_HEIGHT }}><Type className="h-3 w-3" />{t('textTrack.lane', { index: index + 1 })}</div>) : null,
      content: items.length ? <TextTrackLanes lanes={lanes} zoom={state.zoomLevel} revision={0} selectedKey={selectedKey} disabled={disabled}
        onSelect={select} onChange={change} onGestureStart={begin} onGestureEnd={finish} /> : null,
    },
  }
}
